import CodeIcon from "@mui/icons-material/Code";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import NotesIcon from "@mui/icons-material/Notes";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import TableChartIcon from "@mui/icons-material/TableChart";
import { SvgIconComponent } from "@mui/icons-material";
import {
  ACCEPTED_ATTACHMENT_EXTENSIONS,
  canonicalAttachmentExtension,
} from "./constants";

const ICONS_BY_EXTENSION: Record<string, SvgIconComponent> = {
  pdf: PictureAsPdfIcon,
  doc: DescriptionIcon,
  docx: DescriptionIcon,
  xls: TableChartIcon,
  xlsx: TableChartIcon,
  jpg: ImageIcon,
  png: ImageIcon,
  gif: ImageIcon,
  tif: ImageIcon,
  txt: NotesIcon,
  xml: CodeIcon,
  html: CodeIcon,
};

export const isAcceptedAttachmentFilename = (filename: string): boolean => {
  const ext = `.${canonicalAttachmentExtension(filename)}`;
  return (ACCEPTED_ATTACHMENT_EXTENSIONS as readonly string[]).includes(ext);
};

const hasForbiddenFilenameChar = (name: string): boolean => {
  for (let index = 0; index < name.length; index += 1) {
    const code = name.charCodeAt(index);
    if (
      code <= 31 ||
      code === 127 ||
      name[index] === "/" ||
      name[index] === "\\"
    ) {
      return true;
    }
  }
  return false;
};

/** Same filename rules as the messaging upload query. */
export const isAcceptableAttachmentFilename = (filename: string): boolean => {
  const name = filename.trim();
  return (
    name.length > 0 &&
    name.length <= 255 &&
    name !== "." &&
    name !== ".." &&
    !hasForbiddenFilenameChar(name)
  );
};

const MIME_BY_EXTENSION: Record<string, string> = {
  pdf: "application/pdf",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  jpg: "image/jpeg",
  png: "image/png",
  gif: "image/gif",
  tif: "image/tiff",
  txt: "text/plain",
  xml: "application/xml",
  html: "text/html",
};

const CONTENT_TYPE_PATTERN = /^[\w.+-]+\/[\w.+-]+$/;

export const attachmentContentType = (file: {
  name: string;
  type: string;
}): string => {
  if (file.type && CONTENT_TYPE_PATTERN.test(file.type)) return file.type;
  return (
    MIME_BY_EXTENSION[canonicalAttachmentExtension(file.name)] ??
    "application/octet-stream"
  );
};

export const getAttachmentIcon = (filename: string): SvgIconComponent =>
  ICONS_BY_EXTENSION[canonicalAttachmentExtension(filename)] ??
  InsertDriveFileIcon;

export const formatAttachmentSize = (size: number): string => {
  if (size >= 1000 * 1000) {
    return `${(size / (1000 * 1000)).toFixed(2)} MB`;
  }
  if (size >= 1000) {
    return `${Math.round(size / 1000)} KB`;
  }
  return `${size} B`;
};
